<template>
  <div class="card border mb-3">
    <div
      class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2"
    >
      <div>
        <h5 class="mb-0">{{ $t("forwards.deals.pap.schedule_title") }}</h5>
        <span class="text-body-tertiary fs-10">{{ windowText }}</span>
      </div>
      <div v-if="!editing" class="d-flex align-items-center gap-2">
        <button
          v-if="canEdit || canCorrectShipped"
          type="button"
          class="btn btn-sm btn-ghost"
          :disabled="scheduleRows.length === 0"
          @click="startEditing"
        >
          {{ $t("forwards.deals.pap.edit_schedule") }}
        </button>
        <ActionButtonWithSpinner
          v-if="canCancel"
          :busy="busyAction === 'cancel'"
          :disabled="busyAction !== null"
          variant="btn-outline-danger"
          @click="emit('cancel')"
        >
          {{ $t("forwards.deals.plan.cancel_deal") }}
        </ActionButtonWithSpinner>
      </div>
    </div>

    <div class="card-body">
      <p v-if="pools.length > 0" class="mb-2" data-testid="pap-pools">
        {{ $t("forwards.deals.pap.pools", pools.length) }}:
        <template v-for="(pool, index) in pools" :key="pool.pool_id">
          <template v-if="index > 0">, </template>
          <PoolLinkComponent :poolId="pool.pool_id">{{
            pool.pool_name
          }}</PoolLinkComponent>
        </template>
      </p>

      <div
        v-if="editing && mergeNotice"
        class="alert alert-warning py-2 mb-3"
        role="status"
        data-testid="schedule-merge-notice"
      >
        <p v-if="mergeUnaffected" class="mb-0">
          {{ $t("forwards.deals.merge.unaffected") }}
        </p>
        <p v-else class="mb-1">
          {{ $t("forwards.deals.merge.changed_meanwhile") }}
        </p>
        <ul v-if="!mergeUnaffected" class="mb-0 ps-3">
          <li v-if="mergeNotice.takenOver > 0">
            {{ $t("forwards.deals.merge.taken_over", mergeNotice.takenOver) }}
          </li>
          <li v-if="mergeNotice.conflicts > 0">
            {{ $t("forwards.deals.merge.conflicts", mergeNotice.conflicts) }}
          </li>
          <li v-if="mergeNotice.shippedMonths.length > 0">
            {{
              $t(
                "forwards.deals.pap.months_shipped",
                { months: shippedMonthsText },
                mergeNotice.shippedMonths.length
              )
            }}
          </li>
        </ul>
      </div>

      <p class="mb-2">
        {{
          $t("forwards.deals.pap.tolerance", { percent: tolerancePercentText })
        }}
      </p>

      <p v-if="months.length === 0" class="text-body-tertiary mb-0">
        {{ $t("forwards.deals.pap.no_schedule") }}
      </p>

      <table v-else class="table table-sm mb-0">
        <thead>
          <tr>
            <th scope="col">{{ $t("forwards.deals.pap.month") }}</th>
            <th scope="col" class="text-end target-cell">
              {{ $t("forwards.deals.pap.nominated") }}
            </th>
            <th scope="col" class="text-end price-cell">
              {{ $t("forwards.deals.pap.sell_price") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="month in scheduleRows"
            :key="month.month"
            :data-month="month.month"
          >
            <td class="text-nowrap">{{ formatMonthYear(month.month) }}</td>
            <td class="text-end target-cell">
              <DraftConflictComponent
                v-if="editing && conflicts[targetField(month.month)]"
                :mine="mwhText(conflicts[targetField(month.month)].mine)"
                :theirs="mwhText(conflicts[targetField(month.month)].theirs)"
                @pick="pick(targetField(month.month), $event)"
              />
              <MwhInputComponent
                v-else-if="editing && canEdit && !month.shipped"
                v-model="draft[targetField(month.month)]"
                size="sm"
                :min="0"
                :ariaLabel="
                  $t('forwards.deals.pap.target_for', {
                    month: formatMonthYear(month.month),
                  })
                "
              />
              <span v-else>{{ mwhText(month.target) }}</span>
            </td>
            <td class="text-end text-nowrap price-cell">
              <DraftConflictComponent
                v-if="editing && conflicts[priceField(month.month)]"
                :mine="priceText(conflicts[priceField(month.month)].mine)"
                :theirs="priceText(conflicts[priceField(month.month)].theirs)"
                @pick="pick(priceField(month.month), $event)"
              />
              <template v-else-if="editing && isPriceEditable(month, access)">
                <MwhInputComponent
                  v-model="draft[priceField(month.month)]"
                  size="sm"
                  unit="€/MWh"
                  :step="0.001"
                  :invalid="isPriceInvalid(month)"
                  :ariaLabel="
                    $t('forwards.deals.pap.sell_price_for', {
                      month: formatMonthYear(month.month),
                    })
                  "
                />
                <div
                  v-if="isPriceInvalid(month)"
                  class="text-danger fs-10 text-wrap"
                >
                  {{ $t("forwards.deals.pap.sell_price_invalid") }}
                </div>
                <div
                  v-for="warning in correctionCaveatsFor(month)"
                  :key="warning"
                  class="text-warning-emphasis fs-10 text-wrap text-start"
                  data-testid="correction-warning"
                >
                  {{ warning }}
                </div>
              </template>
              <span v-else>{{ priceText(month.price) }}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="editing" class="mt-3">
        <template v-if="canEdit">
          <label class="form-label" :for="toleranceId">{{
            $t("forwards.deals.pap.tolerance_label")
          }}</label>
          <DraftConflictComponent
            v-if="conflicts[TOLERANCE_FIELD]"
            :mine="percentText(conflicts[TOLERANCE_FIELD].mine)"
            :theirs="percentText(conflicts[TOLERANCE_FIELD].theirs)"
            @pick="pick(TOLERANCE_FIELD, $event)"
          />
          <input
            v-else
            :id="toleranceId"
            v-model="tolerancePercent"
            type="number"
            step="0.1"
            min="0"
            :max="PERCENT"
            class="form-control form-control-sm tolerance-input"
          />
          <p class="form-text">
            {{ $t("forwards.deals.pap.tolerance_hint") }}
          </p>
        </template>
        <p
          v-if="canCorrectShipped"
          class="form-text"
          data-testid="correction-hint"
        >
          {{ $t("forwards.deals.correction.schedule_hint") }}
        </p>

        <p v-if="errorMessage" class="error-note mb-2" role="alert">
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>{{ errorMessage }}</span>
        </p>

        <div class="d-flex justify-content-end gap-2">
          <button
            type="button"
            class="btn btn-sm btn-link text-secondary"
            @click="editing = false"
          >
            {{ $t("forwards.deals.common.cancel") }}
          </button>
          <ActionButtonWithSpinner
            :busy="busy"
            :disabled="busy || !canSave"
            variant="btn-primary"
            @click="save"
          >
            {{ $t("forwards.deals.pap.save_schedule") }}
          </ActionButtonWithSpinner>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import PoolLinkComponent from "@/forwards2/components/PoolLinkComponent.vue";
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import {
  formatDate,
  formatMonthYear,
  formatNumberWithSpaces,
} from "@/composables/formatUtils";
import { useUnsavedChangesGuard } from "@/composables/useUnsavedChangesGuard";
import MwhInputComponent from "@/forwards2/components/MwhInputComponent.vue";
import DraftConflictComponent from "@/forwards2/deals/DraftConflictComponent.vue";
import { type DraftConflict, draftDiffers } from "@/forwards2/deals/draftMerge";
import { correctionCaveats } from "@/forwards2/deals/historyCorrection";
import {
  isPriceEditable,
  isSchedulePriceValid,
  mergeSchedule,
  priceField,
  type ScheduleAccess,
  type ScheduleDraft,
  type ScheduleMonth,
  type ScheduleValue,
  savedSchedule,
  scheduleMonths,
  scheduleRequest,
  targetField,
  TOLERANCE_FIELD,
} from "@/forwards2/deals/scheduleDraft";
import type { DealAction } from "@/forwards2/deals/useDealStore";
import type {
  CommitmentGroup,
  DeliveryResponse,
  PapMonthStatus,
  PapScheduleUpdateRequest,
} from "@/forwards2/forwardsTypes";
import { PERCENT, toPercentText } from "@/forwards2/percentUtils";
import { priceRangeLabel } from "@/forwards2/priceUtils";
import { omit } from "lodash-es";
import { computed, ref, useId } from "vue";
import { useI18n } from "vue-i18n";

/** What the card says after a 409 merge, until the edit closes. */
interface MergeNotice {
  takenOver: number;
  conflicts: number;
  shippedMonths: string[];
}

/**
 * The nominated monthly schedule of a pay-as-produced deal, the band around it, and what each
 * month sells at.
 *
 * The schedule is entered, never projected (plan Appendix D.1 row 1h): it is what the contract
 * promised the buyer, and the monitoring table below compares real production against it. Each
 * month is one delivery, so its sell price is that delivery's price; the card is the one place it
 * is set, saved with the nominations and the tolerance in one write (user decision, 2026-09-28).
 * A shipped month is history: its nomination stays read-only, and only a super admin may correct
 * its price (`canCorrectShipped`), on a finished contract too, told which invoices keep the old one
 * (user decision, 2026-10-08).
 *
 * The save carries the `plan_version` the edit started from. When someone else saved meanwhile,
 * the parent reloads the deal and calls `mergeSavedSchedule`: the draft is kept and merged field by
 * field onto what is saved now (`scheduleDraft.mergeSchedule`), a field both sides changed
 * differently waits for the user to pick, and Save then retries against the new version.
 */
const props = withDefaults(
  defineProps<{
    windowStart: string | null;
    windowEnd: string | null;
    /** Decimal fraction as a string, e.g. `"0.1"`. */
    tolerance: string | null;
    months: PapMonthStatus[];
    /** The deal's deliveries, which hold each month's saved target and price. */
    deliveries: DeliveryResponse[];
    /** `DealResponse.plan_version`, the stamp a save is composed against. */
    planVersion: number;
    /** The pools backing the contract, from the deal's commitment groups. */
    pools?: Pick<CommitmentGroup, "pool_id" | "pool_name">[];
    /** `DealCapabilities.can_edit_pap_schedule` from the admin write access check. */
    canEdit: boolean;
    /** `DealCapabilities.can_correct_shipped`: a shipped month's price is open to this session. */
    canCorrectShipped?: boolean;
    /**
     * `DealCapabilities.can_cancel`. A pay-as-produced deal has no plan summary bar, so its Cancel
     * sits here beside Edit.
     */
    canCancel?: boolean;
    busy?: boolean;
    /** The deal action in flight; Cancel waits for any of them, as the plan summary bar's does. */
    busyAction?: DealAction | null;
    errorMessage?: string | null;
  }>(),
  {
    pools: () => [],
    canCorrectShipped: false,
    canCancel: false,
    busy: false,
    busyAction: null,
    errorMessage: null,
  }
);

const emit = defineEmits<{
  save: [body: PapScheduleUpdateRequest];
  cancel: [];
}>();

/**
 * Owned by the parent, which closes the editor only once the save has landed: a renomination the
 * backend refuses (a completed month) must keep the entered numbers on screen (RV-pap-07).
 */
const editing = defineModel<boolean>("editing", { required: true });

const { t } = useI18n();

const toleranceId = useId();
const draft = ref<ScheduleDraft>({});
/** The saved values the draft is composed against, and their `plan_version`. */
const base = ref<ScheduleDraft>({});
const baseVersion = ref(0);
const conflicts = ref<Record<string, DraftConflict<ScheduleValue>>>({});
const mergeNotice = ref<MergeNotice | null>(null);

const scheduleRows = computed<ScheduleMonth[]>(() =>
  scheduleMonths(props.months, props.deliveries)
);

const access = computed<ScheduleAccess>(() => ({
  schedule: props.canEdit,
  correctsShipped: props.canCorrectShipped,
}));

const windowText = computed(() =>
  props.windowStart && props.windowEnd
    ? t("forwards.deals.pap.window", {
        start: formatDate(props.windowStart),
        end: formatDate(props.windowEnd),
      })
    : t("forwards.deals.pap.no_window")
);

const tolerancePercentText = computed(() =>
  props.tolerance === null
    ? t("forwards.common.none")
    : toPercentText(props.tolerance)
);

/** The tolerance input's text; the draft holds the percent as a number, null when empty. */
const tolerancePercent = computed({
  get: () => {
    const percent = draft.value[TOLERANCE_FIELD];
    return percent === null || percent === undefined ? "" : String(percent);
  },
  set: (text: string | number) => {
    draft.value[TOLERANCE_FIELD] = text === "" ? null : Number(text);
  },
});

/** Someone else's save touched nothing on the card (another delivery's prices, say): only Save again is needed. */
const mergeUnaffected = computed(
  () =>
    mergeNotice.value !== null &&
    mergeNotice.value.takenOver === 0 &&
    mergeNotice.value.conflicts === 0 &&
    mergeNotice.value.shippedMonths.length === 0
);

const shippedMonthsText = computed(() =>
  (mergeNotice.value?.shippedMonths ?? [])
    .map((month) => formatMonthYear(month))
    .join(", ")
);

const hasInvalidPrice = computed(() => scheduleRows.value.some(isPriceInvalid));

const canSave = computed(
  () => Object.keys(conflicts.value).length === 0 && !hasInvalidPrice.value
);

// An unresolved conflict is unsaved work too, even when the draft reads like the saved values.
useUnsavedChangesGuard(
  () =>
    editing.value &&
    (Object.keys(conflicts.value).length > 0 ||
      draftDiffers(draft.value, base.value))
);

function isPriceInvalid(month: ScheduleMonth): boolean {
  if (!editing.value || !isPriceEditable(month, access.value)) return false;
  const field = priceField(month.month);
  return !isSchedulePriceValid(
    draft.value[field] ?? null,
    base.value[field] ?? null
  );
}

/** A shipped month's corrected price, said as soon as it is typed. */
function correctionCaveatsFor(month: ScheduleMonth): string[] {
  if (!month.shipped) return [];
  const delivery = props.deliveries.find(
    (candidate) => candidate.id === month.deliveryId
  );
  if (delivery === undefined) return [];
  const field = priceField(month.month);
  return correctionCaveats(t, delivery, {
    priceChanged: (draft.value[field] ?? null) !== (base.value[field] ?? null),
    windowChanged: false,
  });
}

const mwhText = (value: ScheduleValue) =>
  t("forwards.common.mwh_value", {
    value: formatNumberWithSpaces(value ?? 0),
  });

/** A price at the column's three decimals, as the deal reports a saved one. */
const priceText = (value: ScheduleValue) => {
  const price = value === null ? null : value.toFixed(3);
  return priceRangeLabel(t, price, price);
};

const percentText = (value: ScheduleValue) =>
  value === null ? t("forwards.common.none") : `${value} %`;

const startEditing = () => {
  base.value = savedSchedule(scheduleRows.value, props.tolerance);
  draft.value = { ...base.value };
  baseVersion.value = props.planVersion;
  conflicts.value = {};
  mergeNotice.value = null;
  editing.value = true;
};

const save = () => {
  if (!canSave.value) return;
  emit(
    "save",
    scheduleRequest(
      draft.value,
      scheduleRows.value,
      baseVersion.value,
      access.value
    )
  );
};

const pick = (field: string, side: "mine" | "theirs") => {
  const conflict = conflicts.value[field];
  if (conflict === undefined) return;
  draft.value[field] = conflict[side];
  conflicts.value = omit(conflicts.value, field);
};

/**
 * Merges the draft onto the schedule as the props now show it, after the parent reloaded the deal
 * for a stale save. What is saved now becomes the base, so the next Save retries against it; a
 * second conflict merges again the same way.
 */
const mergeSavedSchedule = () => {
  const merged = mergeSchedule(
    base.value,
    draft.value,
    scheduleRows.value,
    props.tolerance,
    access.value
  );
  draft.value = merged.draft;
  conflicts.value = merged.conflicts;
  base.value = savedSchedule(scheduleRows.value, props.tolerance);
  baseVersion.value = props.planVersion;
  mergeNotice.value = {
    takenOver: merged.takenOver,
    conflicts: Object.keys(merged.conflicts).length,
    shippedMonths: merged.shippedMonths,
  };
};

defineExpose({ mergeSavedSchedule });
</script>

<style scoped lang="scss">
.target-cell {
  min-width: 9rem;
}

.price-cell {
  min-width: 10rem;
}

.tolerance-input {
  max-width: 8rem;
}
</style>
